import React, { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  IconChevronLeft,
  IconChevronRight,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";
import { useGesture } from "@use-gesture/react";
import styles from "./document.module.css";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

interface PDFPreviewProps {
  fileUrl: string;
  className?: string;
  onError?: (e: Error) => void;
  controlsPosition?: "overlay" | "bottom";
  onLoadSuccess?: (numPages: number) => void;
  variant?: "default" | "emergency-card"; // New prop to differentiate emergency card styling
}

const MIN_SCALE = 0.5;
const MAX_SCALE = 4;
const clamp = (v: number, min: number, max: number) =>
  Math.min(Math.max(v, min), max);

const PDFPreview: React.FC<PDFPreviewProps> = ({
  fileUrl,
  className = "",
  onError,
  controlsPosition = "overlay",
  onLoadSuccess,
  variant = "default",
}) => {
  const [numPages, setNumPages] = useState<number>();
  const [pageNumber, setPageNumber] = useState<number>(1);
  // `scale` is only for the zoom % display and button state — NOT for react-pdf rendering.
  // All actual zoom is applied via CSS transform on pageWrapRef.
  const [scale, setScale] = useState<number>(1.0);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  const containerRef = useRef<HTMLDivElement>(null);
  // spacerRef: explicitly sized div that sets the scroll extent to the zoomed dimensions.
  // Without this, CSS transform doesn't affect layout so the container can't scroll properly.
  const spacerRef = useRef<HTMLDivElement>(null);
  const pageWrapRef = useRef<HTMLDivElement>(null);

  // Live scale ref — updated on every gesture frame without triggering React re-renders.
  const scaleRef = useRef<number>(1.0);

  // Natural page size at scale=1 (captured once after first render).
  const naturalSizeRef = useRef({ w: 0, h: 0 });
  const [naturalSizeKnown, setNaturalSizeKnown] = useState(false);

  // Pinch gesture initial state — set on first frame.
  const gestureRef = useRef<{
    startScale: number;
    startScrollLeft: number;
    startScrollTop: number;
    pinchCX: number; // pinch center X relative to container viewport
    pinchCY: number;
  } | null>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    setContainerWidth(el.offsetWidth);
    const ro = new ResizeObserver((e) =>
      setContainerWidth(e[0].contentRect.width)
    );
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const handlePrev = useCallback(
    () => setPageNumber((p) => Math.max(p - 1, 1)),
    []
  );
  const handleNext = useCallback(
    () => setPageNumber((p) => (numPages ? Math.min(p + 1, numPages) : p)),
    [numPages]
  );

  /**
   * Apply a new zoom level directly to the DOM — no React state update.
   * This runs at 60fps during the gesture without triggering re-renders.
   *
   * The scroll formula: to keep the pinch center (CX, CY) stationary in the viewport
   * as scale changes by `ratio`, scroll must scale proportionally:
   *   newScroll = (oldScroll + C) * ratio - C
   */
  const applyScaleDOM = useCallback(
    (newScale: number, scrollLeft: number, scrollTop: number) => {
      const el = pageWrapRef.current;
      const spacer = spacerRef.current;
      const container = containerRef.current;
      if (!el || !spacer || !container) return;

      // Always force top-left origin regardless of naturalSizeKnown state
      el.style.transformOrigin = "top left";
      el.style.transform = `scale(${newScale})`;
      // Keep spacer sized to the zoomed content so scroll extent stays correct
      if (naturalSizeRef.current.w) {
        spacer.style.width = `${naturalSizeRef.current.w * newScale}px`;
        spacer.style.height = `${naturalSizeRef.current.h * newScale}px`;
      }
      container.scrollLeft = Math.max(0, scrollLeft);
      container.scrollTop = Math.max(0, scrollTop);

      scaleRef.current = newScale;
    },
    []
  );

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setPageNumber(1);
    onLoadSuccess?.(numPages);
  }

  /** Capture the natural page size once after the first page render. */
  function onPageRenderSuccess() {
    if (!naturalSizeRef.current.w && pageWrapRef.current) {
      const { offsetWidth: w, offsetHeight: h } = pageWrapRef.current;
      naturalSizeRef.current = { w, h };
      setNaturalSizeKnown(true);
    }
  }

  const bind = useGesture(
    {
      onPinch: ({ offset: [s], origin: [ox, oy], first }) => {
        const newScale = clamp(s, MIN_SCALE, MAX_SCALE);
        const container = containerRef.current;
        if (!container) return;

        if (first) {
          const rect = container.getBoundingClientRect();
          gestureRef.current = {
            startScale: scaleRef.current,
            startScrollLeft: container.scrollLeft,
            startScrollTop: container.scrollTop,
            pinchCX: ox - rect.left,
            pinchCY: oy - rect.top,
          };
        }

        const g = gestureRef.current;
        if (!g) return;

        const ratio = newScale / g.startScale;
        applyScaleDOM(
          newScale,
          (g.startScrollLeft + g.pinchCX) * ratio - g.pinchCX,
          (g.startScrollTop + g.pinchCY) * ratio - g.pinchCY
        );
      },

      onPinchEnd: ({ offset: [s] }) => {
        const finalScale = clamp(s, MIN_SCALE, MAX_SCALE);
        gestureRef.current = null;
        // Commit to React state for display only — DOM is already correct from applyScaleDOM.
        // No timing issue because react-pdf scale prop is never changed (no canvas re-render).
        setScale(finalScale);
        scaleRef.current = finalScale;
      },

      onDrag: ({ delta: [dx, dy], swipe: [swipeX], tap, last }) => {
        if (tap) return;
        const isZoomed = scaleRef.current > 1.01;
        if (isZoomed) {
          // Pan the document — finger moves right so content scrolls left
          const container = containerRef.current;
          if (container) {
            container.scrollLeft = Math.max(0, container.scrollLeft - dx);
            container.scrollTop = Math.max(0, container.scrollTop - dy);
          }
        } else if (last && (numPages ?? 0) > 1) {
          // Swipe to change page only when at 100% zoom
          if (swipeX === -1) handleNext();
          if (swipeX === 1) handlePrev();
        }
      },
    },
    {
      pinch: {
        scaleBounds: { min: MIN_SCALE, max: MAX_SCALE },
        from: () => [scaleRef.current, 0],
        rubberband: true,
      },
      drag: {
        filterTaps: true,
        // No axis constraint — need both x and y for panning when zoomed
        swipe: { distance: [40, 40], velocity: [0.25, 0.25] },
      },
    }
  );

  const zoomByButton = (next: number) => {
    const container = containerRef.current;
    if (container) {
      const cx = container.clientWidth / 2;
      const cy = container.clientHeight / 2;
      const ratio = next / scaleRef.current;
      applyScaleDOM(
        next,
        (container.scrollLeft + cx) * ratio - cx,
        (container.scrollTop + cy) * ratio - cy
      );
    }
    setScale(next);
    scaleRef.current = next;
  };

  const handleZoomIn = () =>
    zoomByButton(
      clamp(+(scaleRef.current + 0.25).toFixed(2), MIN_SCALE, MAX_SCALE)
    );
  const handleZoomOut = () =>
    zoomByButton(
      clamp(+(scaleRef.current - 0.25).toFixed(2), MIN_SCALE, MAX_SCALE)
    );

  const isOverlay = controlsPosition === "overlay";
  const isEmergencyCard = variant === "emergency-card";

  const paginationControls = (
    <div
      className={
        isOverlay
          ? styles.pdfPaginationOverlay
          : isEmergencyCard
          ? styles.pdfPaginationBottomBar
          : styles.pdfPagination
      }
      style={{ visibility: numPages && numPages > 1 ? "visible" : "hidden" }}
    >
      {isEmergencyCard ? (
        <>
          <button
            type="button"
            onClick={handlePrev}
            disabled={pageNumber <= 1}
            className={styles.paginationIconBtn}
            aria-label="Previous page"
          >
            <IconChevronLeft size={20} />
          </button>
          <span className={styles.paginationTextCompact}>
            {pageNumber} of {numPages || 1}
          </span>
          <button
            type="button"
            onClick={handleNext}
            disabled={pageNumber >= (numPages || 1)}
            className={styles.paginationIconBtn}
            aria-label="Next page"
          >
            <IconChevronRight size={20} />
          </button>
        </>
      ) : (
        <>
          <button
            onClick={handlePrev}
            disabled={pageNumber <= 1}
            className={styles.paginationBtn}
          >
            Prev
          </button>
          <span className={styles.paginationText}>
            Page {pageNumber} of {numPages || 1}
          </span>
          <button
            onClick={handleNext}
            disabled={pageNumber >= (numPages || 1)}
            className={styles.paginationBtn}
          >
            Next
          </button>
        </>
      )}
    </div>
  );

  const zoomControls = (
    <div
      className={isOverlay ? styles.zoomOverlay : styles.zoomBottom}
      style={{ display: isEmergencyCard ? "none" : undefined }}
    >
      <button
        className={scale > MIN_SCALE ? styles.zoomBtn : styles.zoomBtnDisabled}
        onClick={handleZoomOut}
        aria-label="Zoom out"
        disabled={scale <= MIN_SCALE}
      >
        <IconZoomOut size={22} />
      </button>
      <span className={styles.zoomPercent}>{Math.round(scale * 100)}%</span>
      <button
        className={scale < MAX_SCALE ? styles.zoomBtn : styles.zoomBtnDisabled}
        onClick={handleZoomIn}
        aria-label="Zoom in"
        disabled={scale >= MAX_SCALE}
      >
        <IconZoomIn size={22} />
      </button>
    </div>
  );

  const { w: nw, h: nh } = naturalSizeRef.current;

  return (
    <div
      className={`w-full flex flex-col items-center ${styles.pdfPreview} ${
        isEmergencyCard ? styles.emergencyCardPdfPreview : ""
      } ${className}`}
    >
      {isOverlay && (
        <div className={styles.overlayControlsContainer}>
          {paginationControls}
          {zoomControls}
        </div>
      )}

      <div
        ref={containerRef}
        className={styles.pdfContainer}
        style={{
          touchAction: "none",
          overflow: "auto",
          // Fixed height is required for vertical scroll to work.
          // Without it the container grows to fit content, scrollTop can never be set.
          height: isEmergencyCard
            ? "calc(100vh - 210px)"
            : "calc(100vh - 280px)",
        }}
        {...bind()}
      >
        {/*
          Spacer: creates the correct scroll extent for the zoomed content.
          Once natural page size is known, it's explicitly sized to nw*scale × nh*scale.
          The page inside is absolutely positioned and CSS-transformed from top-left.

          Before natural size is known: page is relative-positioned so the spacer
          collapses to the page's natural height — no zoom yet, so this is correct.
        */}
        <div
          ref={spacerRef}
          style={
            naturalSizeKnown
              ? {
                  position: "relative",
                  flexShrink: 0,
                  width: nw * scale,
                  height: nh * scale,
                }
              : { position: "relative", flexShrink: 0, width: "100%" }
          }
        >
          <div
            ref={pageWrapRef}
            style={
              naturalSizeKnown
                ? {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    transformOrigin: "top left",
                    transform: `scale(${scale})`,
                    willChange: "transform",
                  }
                : { position: "relative", willChange: "transform" }
            }
          >
            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onError}
              loading={
                <span className="text-gray-400 text-base">Loading ...</span>
              }
              error={
                <span className="text-red-500 text-base">Failed to load.</span>
              }
            >
              <Page
                pageNumber={pageNumber}
                renderTextLayer={false}
                renderAnnotationLayer={false}
                width={containerWidth || undefined}
                onRenderSuccess={onPageRenderSuccess}
                className={styles.pdfPageContent}
              />
            </Document>
          </div>
        </div>
      </div>

      {!isOverlay && (
        <div className={styles.bottomControls}>
          {paginationControls}
          {zoomControls}
        </div>
      )}
    </div>
  );
};

export default PDFPreview;
